import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signUp, signInWithGoogle } = useAuth();

  const validateForm = () => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const result = await signUp(email, password, name || undefined);

      // Check if registration requires approval
      if (result && result.isPending) {
        // User needs approval - redirect to waitlist
        navigate('/waitlist-confirmation');
      } else {
        // User is auto-approved - redirect to onboarding
        navigate('/onboarding/step-1');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign up');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in with Google');
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#ffffeb] flex items-center justify-center px-4 py-8"
      style={{
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
          repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
        `
      }}
    >
      <div className="w-full max-w-md">
        <div
          className="bg-white border-4 border-[#077f5d] p-8"
          style={{ boxShadow: '6px 6px 0 #077f5d' }}
        >
          {/* Logo Header */}
          <div className="flex items-center justify-center mb-8">
            <div
              className="bg-[#3cbe8c] border-4 border-[#077f5d] p-1s"
              style={{ boxShadow: '6px 6px 0 #077f5d' }}
            >
              <picture>
                <source srcSet="/Fittory.avif" type="image/avif" />
                <source srcSet="/Fittory.webp" type="image/webp" />
                <img
                  src="/Fittory.png"
                  alt="Fittory"
                  className="h-16 w-auto"
                />
              </picture>
            </div>
          </div>

          <h1
            className="text-2xl text-center text-[#077f5d] mb-4 uppercase"
            style={{ fontFamily: "'Press Start 2P'", lineHeight: '1.3' }}
          >
            Daftar
          </h1>

          {error && (
            <div
              className="bg-red-500 border-3 border-red-700 px-4 py-3 mb-4 font-mono text-sm text-white"
              style={{ boxShadow: '4px 4px 0 #c0392b' }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
                style={{ boxShadow: '4px 4px 0 #077f5d' }}
                placeholder="Your name"
              />
            </div>

            <div className="space-y-2">
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
                style={{ boxShadow: '4px 4px 0 #077f5d' }}
                placeholder="your@email.com"
              />
            </div>

            <div className="space-y-2">
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
                style={{ boxShadow: '4px 4px 0 #077f5d' }}
                placeholder="Minimum 8 characters"
              />
            </div>

            <div className="space-y-2">
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
                style={{ boxShadow: '4px 4px 0 #077f5d' }}
                placeholder="Confirm your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#3cbe8c] text-white px-8 py-4 border-2 border-[#077f5d] uppercase font-bold"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '10px',
                boxShadow: '6px 6px 0 #077f5d'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                  e.currentTarget.style.transform = 'translate(-2px, -2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(0, 0)';
              }}
            >
              {loading ? 'Mendaftar...' : 'Daftar'}
            </button>
          </form>

          <div className="my-4 flex items-center mb-2">
            <div className="flex-1 border-t-2 border-[#077f5d]"></div>
            <span
              className="px-4 text-[#077f5d] uppercase"
              style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '8px' }}
            >
              OR
            </span>
            <div className="flex-1 border-t-2 border-[#077f5d]"></div>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full bg-white text-[#077f5d] px-6 py-3 border-2 border-[#077f5d] font-bold flex items-center justify-center gap-2"
            style={{ boxShadow: '4px 4px 0 #077f5d', fontSize: '12px' }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Lanjut dengan Google
          </button>

          <p className="mt-6 text-center text-sm font-mono text-[#1a1a1a]">
            Sudah punya akun?{' '}
            <Link
              to="/login"
              className="text-[#077f5d] font-bold hover:text-[#3cbe8c]"
            >
              Masuk
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
